/**
 * Prepare one default-column commit before any DOM application.
 * Owns dual compilation, structure referee evidence, breaker announcement and
 * selected-program classification. The binder owns host effects and publication.
 */
import type { Option, RenderFlags } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";
import { classifyCellDirectives, compileCommitBundle, compileSceneCommit } from "./commit-bundle.ts";
import type { CellDirective, CommitBundle, SceneCommitResult } from "./commit-bundle.ts";
import { refereeCommit } from "./commit-referee.ts";
import type { RefereeVerdict } from "./commit-referee.ts";
import type { LiveSceneRunner } from "./live-scene-runner.ts";
import { selectStructureSource } from "./referee-ledger.ts";
import type { RefereeLedger } from "./referee-ledger.ts";

/** A station port, not ownership of scene retention or DOM host callbacks. */
type CommitSceneStation = Pick<LiveSceneRunner,
  "build" | "recordSceneFailure" | "recordSceneThrew" | "recordReferee" | "snapshot">;
type CommitRefereeLedger = Pick<RefereeLedger,
  "recordCommit" | "tripBreaker" | "snapshot" | "breakerTripped">;

export type ColumnCommitInput = {
  readonly sceneInput: LiveSceneRunner.BuildInput;
  readonly positionalInput: Parameters<typeof compileCommitBundle>[0];
  readonly sceneStation: CommitSceneStation;
  readonly referees: CommitRefereeLedger;
  /** Read at each original decision site; do not capture a stale sheet flag. */
  readonly currentFlags: () => RenderFlags;
};

export type PreparedColumnCommit = {
  readonly builtScene: Option<LiveSceneRunner.Built>;
  readonly bundle: CommitBundle;
  readonly directives: Option<readonly CellDirective[]>;
  readonly sceneUsed: boolean;
  readonly commitVerdict: Option<RefereeVerdict>;
};

export function prepareColumnCommit(input: ColumnCommitInput): PreparedColumnCommit {
  const { sceneStation, referees, currentFlags } = input;
  const { sheet, column: defaultColumn, rows, plans, bindings } = input.sceneInput;
  const { changes: changesets, oldRowIds, targetKeys: targetRowIds } = input.positionalInput;
  let builtScene: Option<LiveSceneRunner.Built> = null;
  let sceneUsedThisPass = false;
  let commitVerdict: Option<RefereeVerdict> = null;
  let directives: Option<readonly CellDirective[]> = null;
  // Build and compile the scene from the same hoisted rows, plans and bindings.
  // A failed build or compile leaves this pass without a scene bundle; the
  // positional bundle remains available for live rendering and audit.
  let sceneBundle: Option<CommitBundle> = null;
  let sceneBootstrap = false;
  try {
    builtScene = sceneStation.build({
      sheet,
      column: defaultColumn,
      rows,
      plans,
      bindings,
    });
    if (builtScene != null) {
      const sceneResult: SceneCommitResult = compileSceneCommit({
        sceneDiff: builtScene.diff,
        targetKeys: targetRowIds,
        poolRids: oldRowIds,
        prevSceneKeys: builtScene.prevCellKeys,
      });
      if (sceneResult.ok) {
        sceneBundle = sceneResult.bundle;
        sceneBootstrap = sceneResult.bootstrap;
      } else {
        sceneStation.recordSceneFailure(sceneResult.reason);
      }
    }
  } catch (err) {
    sceneStation.recordSceneThrew();
    builtScene = null;
    sceneBundle = null;
    Tracing.warn("ui")("scene/commit build failed", {
      error: err instanceof Error ? err.message : String(err),
    });
  }

  // Positional bundle (the referee's dark referee half + the default live
  // source). Compute it every pass so the referee always has both sides.
  const positional = compileCommitBundle({
    changes: changesets,
    oldRowIds,
    targetKeys: targetRowIds,
  });
  let bundle: CommitBundle = positional;

  // Referee the two bundles (symmetric) and route the verdict through the
  // station counters + a bounded breadcrumb (≤1 firstDivergence
  // sample) — the scene/parity discipline, now on compiled outcomes.
  if (sceneBundle != null) {
    const referee = refereeCommit(oldRowIds, positional, sceneBundle);
    sceneStation.recordReferee(referee, sceneBootstrap);
    referees.recordCommit(referee, sceneBootstrap);
    commitVerdict = referee.verdict;
    if (referee.verdict === "loud" && referees.tripBreaker()) {
      // The breaker's ONE announcement, at the false→true transition — the
      // scene program would have arranged the DOM differently, so from here
      // the production flag path pins positional until `resetSceneBreaker()`.
      // The dark pipeline keeps running and keeps being refereed; only the
      // SELECTION changes, and only when the flag (not the debug override)
      // is what would have promoted the scene.
      Tracing.err("ui")("scene/commit referee loud — breaker pinned positional", {
        column: defaultColumn.offset,
        firstDivergence: referee.firstDivergence,
        ...referees.snapshot(),
        // The two programs and their common input, so a loud verdict can be
        // replayed from the trace instead of reasoned about from a bounded
        // `firstDivergence` line. Diagnostics-armed sessions only — this is
        // the one announcement per breaker trip, but a dozen keys per side
        // is more than the production breadcrumb budget wants.
        ...(currentFlags().diagnostics()
          ? {
            pool: oldRowIds,
            target: targetRowIds,
            a: positional.structure,
            b: sceneBundle.structure,
          }
          : {}),
      });
    }
    // `exact` is the happy path — one breadcrumb per column per bind whose
    // payload spreads a fresh counters object. Keep it under the diagnostics
    // switch; a `slide-classified` or `loud` verdict is the alarm and stays
    // unconditional. Counters remain pull-based, so nothing is lost.
    if (currentFlags().diagnostics() || referee.verdict !== "exact") {
      Tracing.log("scene/commit-referee", {
        column: defaultColumn.offset,
        verdict: referee.verdict,
        bootstrapped: sceneBootstrap,
        firstDivergence: referee.firstDivergence,
        ...sceneStation.snapshot(),
      });
    }
  }

  // Flag picks the live structure source; the other bundle rides dark. When
  // the scene is promoted but unavailable (compile failed / threw), fall back
  // to the positional bundle for this bind + a loud breadcrumb. Effective =
  // the single SDK 2.0 switch, read fresh each bind — minus the breaker,
  // which pins the live path back to positional once this binder has seen a
  // loud verdict. A measurement session is deliberately NOT governed by the
  // breaker: it must keep showing the real (possibly divergent) scene path.
  const sceneCommitLive = selectStructureSource({
    flag: currentFlags().live() ? "live" : "dormant",
    session: currentFlags().diagnostics() ? "diagnostics" : "production",
    breaker: referees.breakerTripped() ? "tripped" : "closed",
  }) === "scene";
  if (sceneCommitLive && sceneBundle != null) {
    bundle = sceneBundle;
    sceneUsedThisPass = true;
  } else {
    if (sceneCommitLive && sceneBundle == null) {
      Tracing.warn("ui")("scene/commit fell back to positional", {
        column: defaultColumn.offset,
      });
    }
    bundle = positional;
  }

  // Classify the per-cell directives against the SELECTED structure program +
  // the scene diff (needs a built scene). Feeds the dark cell referee below,
  // and — when the SDK 2.0 switch is live — the honor path in applyCells.
  if (builtScene != null) {
    directives = classifyCellDirectives({
      structure: bundle.structure,
      sceneDiff: builtScene.diff,
      targetKeys: targetRowIds,
      bootstrap: sceneBootstrap,
    });
  }

  return { builtScene, bundle, directives, sceneUsed: sceneUsedThisPass, commitVerdict };
}
